
import { useState } from 'react';
import { useProperties } from '@/hooks/useProperties';
import { usePropertyMutations } from '@/hooks/usePropertyMutations';
import { Property } from '@/types/property';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdminPropertyCard from './AdminPropertyCard';
import { Search, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdminPropertiesTab = () => {
  const navigate = useNavigate();
  const { data: properties = [], isLoading, error } = useProperties();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredProperties = properties.filter(property => {
    const locationDisplay = property.neighborhood ? `${property.neighborhood.city}, ${property.neighborhood.state}` : '';
    const matchesSearch = property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         locationDisplay.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'available' && property.available) ||
                         (statusFilter === 'unavailable' && !property.available) ||
                         (statusFilter === 'featured' && property.featured);
    
    return matchesSearch && matchesStatus;
  });

  const handleEdit = (property: Property) => {
    console.log('Navigating to edit property:', property.id);
    navigate(`/admin/properties/edit/${property.id}`);
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error al cargar las propiedades</p>
        <Button onClick={() => window.location.reload()} className="mt-4">
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Propiedades</h2>
        <Button onClick={() => navigate('/admin/properties/create')}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Propiedad
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar por título o ubicación..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="available">Disponibles</SelectItem>
            <SelectItem value="unavailable">No disponibles</SelectItem>
            <SelectItem value="featured">Destacadas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <p className="text-sm text-gray-600">Total</p>
          <p className="text-2xl font-semibold">{properties.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <p className="text-sm text-gray-600">Disponibles</p>
          <p className="text-2xl font-semibold">
            {properties.filter(p => p.available).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <p className="text-sm text-gray-600">Destacadas</p>
          <p className="text-2xl font-semibold">
            {properties.filter(p => p.featured).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <p className="text-sm text-gray-600">Promedio precio</p>
          <p className="text-2xl font-semibold">
            ${properties.length > 0 ? Math.round(properties.reduce((sum, p) => sum + p.price, 0) / properties.length).toLocaleString() : 0}
          </p>
        </div>
      </div>

      {/* Properties Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="bg-gray-200 rounded-lg h-80 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {filteredProperties.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No se encontraron propiedades</p>
              {searchTerm && (
                <Button 
                  variant="outline" 
                  onClick={() => setSearchTerm('')}
                  className="mt-2"
                >
                  Limpiar búsqueda
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProperties.map((property) => (
                <AdminPropertyCard
                  key={property.id}
                  property={property}
                  onEdit={handleEdit}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminPropertiesTab;
